import type { Dayjs } from "@thalamos/common";
import type { ExtendedThalamosUser, UUID } from "@thalamos/aspire-common";

import { apiHooks } from "../api.js";
import { classifyApiFailure } from "../helpers/classifyApiFailure.js";
import type {
  SelectedFilteredOptions,
  WorkMode,
} from "../pages/Caseload/types.js";

export function usePatientTimeline(props: {
  patientId: UUID | undefined;
  page?: number;
  limit?: number;
}) {
  const patientId = props.patientId;
  const [
    {
      data: patientTimeline,
      loading: patientTimelineCompleteLoading,
      response,
      error,
    },
    reloadPatientTimeline,
    // TODO: paging?
  ] = apiHooks.patients.getTimeline(patientId!, {
    limit: props.limit || 10,
    offset: props.page,
  });

  return {
    patientTimeline,
    patientTimelineCompleteLoading,
    reloadPatientTimeline,
    response,
    error,
  };
}

export function useGetFileNotes(formId: string, _user: ExtendedThalamosUser) {
  const [
    { data, loading: fileNotesCompleteLoading, response },
    reloadFileNotes,
  ] = apiHooks.forms.getFileNotes(formId);
  return {
    fileNotes: data,
    fileNotesCompleteLoading,
    response,
    reloadFileNotes,
  };
}

export function useRetriveWorkItems({
  userId,
  contextId,
  mode,
  pageLimit,
  page,
  patientSearchFilter,
  selectedFilteredOptions,
  userSpecifiedFromDate,
  toDate,
}: {
  userId: string;
  contextId: string;
  mode: WorkMode;
  pageLimit: number;
  page: number;
  patientSearchFilter: string;
  selectedFilteredOptions: SelectedFilteredOptions;
  userSpecifiedFromDate: string | undefined;
  toDate: Dayjs | null;
}) {
  const [
    {
      data: work,
      loading: workLoading,
      response: response,
      error: retrieveWorkError,
    },
    reloadWork,
  ] = apiHooks.work.retrieveWork(userId, contextId, mode, {
    limit: pageLimit,
    offset: page * pageLimit,
    patientSearchFilter: patientSearchFilter || undefined,
    selectedFormTemplateIds:
      selectedFilteredOptions.formTemplateIds || undefined,
    selectedSenderNames: selectedFilteredOptions.senderNames || undefined,

    fromDate: userSpecifiedFromDate ? userSpecifiedFromDate : undefined,

    toDate: toDate?.isValid()
      ? toDate?.tz("Europe/London").format("YYYY-MM-DD")
      : undefined,
  });

  const failure = classifyApiFailure(retrieveWorkError, response);

  if (failure?.kind === "fatal") {
    // No partial caseload is worth showing, so a fatal failure goes to the
    // error page rather than an in-place banner.
    throw new Error(failure.message ?? "Unable to retrieve caseload");
  }

  return {
    work,
    workLoading,
    workFailure: failure?.kind === "transient" ? failure : null,
    retryRetrieveWork: reloadWork,
  };
}

export function useMhaStatus(props: {
  patientId: UUID | undefined;
  page?: number;
  limit?: number;
  // Read-only unmerge preview: treat these patients as merge-tree roots so the
  // returned status is what the patient would have after an unmerge.
  patientIdsToTreatAsRootPatients?: string[];
}) {
  const patientId = props.patientId;
  const [
    { data: mhaStatus, loading: mhaStatusCompleteLoading, response },
    reloadMhaStatus,
  ] = apiHooks.patients.getMhaState(patientId!, {
    limit: props.limit || 10,
    offset: props.page || 0,
    patientIdsToTreatAsRootPatients: props.patientIdsToTreatAsRootPatients,
  });

  return {
    mhaStatus,
    mhaStatusCompleteLoading,
    reloadMhaStatus,
    response,
  };
}

/**
 * The resolved Section 136 place of safety for a patient, or `undefined`
 * when `skip` (the viewing org doesn't display MHA status, `patientStateEnabled`
 * — display, not calculation, governs whether this is shown; the data exists
 * regardless).
 */
export function useSection136PlaceOfSafety(props: {
  patientId: UUID | undefined;
  skip: boolean;
}) {
  // No patientId yet is itself a reason to skip — the empty-string fallback
  // is never actually requested in that case, it only satisfies the hook's
  // required-string signature until a real id is available.
  const [{ data: section136PlaceOfSafety, loading }] =
    apiHooks.patients.getSection136PlaceOfSafety(
      props.patientId ?? "",
      props.skip || !props.patientId,
    );

  return {
    section136PlaceOfSafety: section136PlaceOfSafety ?? undefined,
    section136PlaceOfSafetyLoading: loading,
  };
}
